import { counter } from './counterMode';
// ============================================================================
// Clerk — l'identité des TITULAIRES vient de PharmaGestion
// ============================================================================
// PharmaGestion est la maison mère de la suite (RH, Compta, Collect…) et sa
// SEULE source d'identité. Un titulaire s'y connecte, puis choisit son
// application ; Officina ne fait que LIRE cette session.
//
// Actif sur `rh.pharmagestion.fr` — un sous-domaine de `pharmagestion.fr`,
// donc couvert par la clé de production sans rien configurer.
//
// ⚠️ Inerte partout ailleurs, `pharmarh.fr` compris. Ce domaine est le portail
// des SALARIÉS, qui n'ont pas de compte PharmaGestion et se connectent par mot
// de passe. L'y activer aurait exigé de le déclarer « domaine satellite » chez
// Clerk — plan payant, enregistrement DNS, et la connexion se serait quand
// même faite sur pharmagestion.fr : « Users must complete both the sign-in and
// sign-up flows on the primary domain ». Aucun intérêt pour un salarié.
//
// Clerk est chargé depuis le CDN de l'instance : AUCUNE dépendance npm ajoutée
// (donc aucun risque sur le build Vite/Vercel).
// ============================================================================

/** Maison mère : où l'on se connecte, et où l'on retourne en se déconnectant. */
export const HUB_URL = 'https://pharmagestion.fr';
/** Sous-domaine du hub où Officina est servi. */
const HUB_HOST = 'rh.pharmagestion.fr';

const PUBLISHABLE_KEY =
  import.meta.env.VITE_CLERK_PUBLISHABLE_KEY || 'pk_live_Y2xlcmsucGhhcm1hZ2VzdGlvbi5mciQ';
const CLERK_FAPI = import.meta.env.VITE_CLERK_FAPI || 'clerk.pharmagestion.fr';

const TOKEN_KEY = 'officina_token';

declare global {
  interface Window {
    Clerk?: any;
  }
}

function hostname(): string {
  return typeof window !== 'undefined' ? window.location.hostname : '';
}

/** Clerk est-il utilisable ici ? */
export function isClerkEnabled(): boolean {
  // POSTE COMPTOIR — Clerk ne démarre pas du tout sur cette machine.
  //
  // Sans ce garde, l'installation d'un poste se détruisait elle-même : elle
  // ferme la session Clerk, puis `initClerkSso()` repartait au rechargement,
  // ne trouvait plus de session, EFFAÇAIT le jeton et redirigeait la machine
  // vers le hub PharmaGestion. L'écran comptoir s'affichait trois secondes
  // avant de partir sur la page de connexion du hub.
  //
  // Le principe est plus simple que le correctif : un poste comptoir a une
  // identité à lui — celle de la machine — et n'emprunte celle de personne.
  // Une session du hub restée dans ce navigateur ne le regarde pas.
  if (counter.isPost()) return false;
  return !!PUBLISHABLE_KEY && hostname() === HUB_HOST;
}

function loadClerkScript(): Promise<void> {
  return new Promise((resolve, reject) => {
    if (window.Clerk) return resolve();
    const s = document.createElement('script');
    s.async = true;
    s.crossOrigin = 'anonymous';
    s.setAttribute('data-clerk-publishable-key', PUBLISHABLE_KEY);
    s.src = `https://${CLERK_FAPI}/npm/@clerk/clerk-js@5/dist/clerk.browser.js`;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error('Clerk: chargement impossible'));
    document.head.appendChild(s);
  });
}

// La session peut apparaître quelques centaines de ms après Clerk.load().
function waitForSession(clerk: any, maxMs = 5000): Promise<boolean> {
  return new Promise((resolve) => {
    const start = Date.now();
    const tick = () => {
      if (clerk.user && clerk.session) return resolve(true);
      if (Date.now() - start > maxMs) return resolve(false);
      setTimeout(tick, 100);
    };
    tick();
  });
}

let clerkPromise: Promise<any | null> | null = null;

/**
 * À appeler AVANT le rendu React (voir main.tsx) : `useCurrentUser()` n'appelle
 * /api/auth/me que si un jeton existe déjà en localStorage.
 *
 * Sans session, on renvoie au sign-in du hub — c'est la seule porte d'entrée
 * ici, contrairement à `pharmarh.fr` où le mot de passe reste une option.
 */
export function initClerkSso(): Promise<any | null> {
  if (!isClerkEnabled()) return Promise.resolve(null);
  if (clerkPromise) return clerkPromise;

  clerkPromise = (async () => {
    try {
      await loadClerkScript();
      const clerk = window.Clerk;
      await clerk.load();

      const signedIn = await waitForSession(clerk);
      if (!signedIn) {
        try { localStorage.removeItem(TOKEN_KEY); } catch { /* noop */ }
        window.location.replace(
          `${HUB_URL}/sign-in?redirect_url=${encodeURIComponent(`https://${HUB_HOST}/`)}`
        );
        return null;
      }

      const token = await clerk.session.getToken();
      if (token) {
        try { localStorage.setItem(TOKEN_KEY, token); } catch { /* noop */ }
      }
      return clerk;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[clerk]', err);
      return null;
    }
  })();

  return clerkPromise;
}

/**
 * Une session Clerk est-elle ouverte ici et maintenant ?
 *
 * C'est ce qui distingue un TITULAIRE (venu du hub) d'un SALARIÉ (mot de
 * passe) — pas le domaine. Le client HTTP s'en sert pour choisir quel jeton
 * envoyer, et la déconnexion pour savoir où renvoyer la personne.
 */
export function hasClerkSession(): boolean {
  // Même règle qu'au démarrage : sur un poste comptoir, il n'y a pas de
  // session Clerk, même si le hub en rouvre une dans ce navigateur.
  if (counter.isPost()) return false;
  return !!(typeof window !== 'undefined' && window.Clerk?.session);
}

/** Adresse e-mail de la session Clerk, pour les messages d'erreur. */
export function clerkEmail(): string | null {
  return window.Clerk?.user?.primaryEmailAddress?.emailAddress ?? null;
}

/** Jeton Clerk frais (ils expirent vite) — utilisé par client.ts à chaque appel. */
export async function getFreshClerkToken(): Promise<string | null> {
  if (!hasClerkSession()) return null;
  try {
    const token = await window.Clerk.session.getToken();
    if (token) {
      try { localStorage.setItem(TOKEN_KEY, token); } catch { /* noop */ }
    }
    return token ?? null;
  } catch {
    return null;
  }
}

/**
 * Ferme la session Clerk. La redirection est décidée par l'appelant.
 *
 * Regarde `window.Clerk` DIRECTEMENT, pas `hasClerkSession()` : sur un poste
 * comptoir ce dernier répond toujours « non », et l'installation ne fermerait
 * alors jamais la session du hub restée ouverte sur la machine — n'importe qui
 * au comptoir pourrait retourner sur PharmaGestion sous l'identité du
 * titulaire.
 */
export async function clerkSignOut(): Promise<void> {
  try {
    if (typeof window === 'undefined' || !window.Clerk?.session) return;
    await window.Clerk.signOut();
  } catch { /* noop */ }
}
